import React from 'react';
import { SessionBundle } from '@shared/types';
import { formatPrice } from '@shared/utils';
import { getTranslatedField } from '../utils/translationUtils';
import { useI18n } from '../i18n/i18nContext';

type PromotionalItem = SessionBundle & {
  itemType: 'session_bundle' | 'subscription';
  pricingRules?: Array<{
    id: string;
    price: number;
    comparedPrice?: number | null;
    priority: number;
    startDate?: string;
    endDate?: string;
  }>;
};

interface PromotionsTableProps {
  items: PromotionalItem[];
  language: string;
  onPurchase: (id: string) => void;
}

const PromotionsTable: React.FC<PromotionsTableProps> = ({
  items,
  language,
  onPurchase,
}) => {
  const { t } = useI18n();

  const getDescription = (sessionBundle: SessionBundle): string => {
    return getTranslatedField(sessionBundle, 'shortDescription', language) as string || '';
  };

  const getComparedPrice = (item: PromotionalItem): number | null => {
    const now = new Date();
    const activeRule = item.pricingRules?.find(rule => {
      const startValid = !rule.startDate || new Date(rule.startDate) <= now;
      const endValid = !rule.endDate || new Date(rule.endDate) >= now;
      return startValid && endValid && rule.comparedPrice;
    });
    return activeRule?.comparedPrice ? activeRule.comparedPrice / 100 : item.originalPrice || null;
  };

  const hasAnyDiscount = items.some(item => {
    const comparedPrice = getComparedPrice(item);
    return comparedPrice && comparedPrice > item.price;
  });

  return (
    <>
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full table-fixed text-left text-sm whitespace-nowrap">
          <thead className="tracking-wider border-b border-promo-300 bg-promo-200">
            <tr>
              <th scope="col" className="px-4 py-4 font-semibold text-gray-700 w-1/4">{t.pricing.sessionBundle}</th>
              <th scope="col" className="px-4 py-4 font-semibold text-gray-700 text-center">{t.pricing.sessions}</th>
              {hasAnyDiscount && (
                <th scope="col" className="px-4 py-4 font-semibold text-gray-700 text-center">{t.pricing.regularPrice}</th>
              )}
              <th scope="col" className="px-4 py-4 font-semibold text-gray-700 text-center">{t.pricing.price}</th>
              <th scope="col" className="px-4 py-4 font-semibold text-gray-700 text-center">{t.pricing.pricePerSession}</th>
              <th scope="col" className="px-4 py-4 font-semibold text-gray-700 text-right"></th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {items.map((item) => {
              const comparedPrice = getComparedPrice(item);
              const hasComparedPrice = comparedPrice && comparedPrice > item.price;
              const pricePerSession = item.sessions && item.sessions > 0
                ? item.price / item.sessions
                : null;
              const isSubscription = item.type === 'subscription';
              const discountPercent = hasComparedPrice ? Math.round((1 - item.price / comparedPrice!) * 100) : 0;

              return (
                <tr key={item.id} className="border-b border-promo-200 odd:bg-white even:bg-promo-50">
                  <td className="px-4 py-4 w-1/4">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{getTranslatedField(item, 'name', language)}</span>
                      {isSubscription && (
                        <span className="inline-flex px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                          {t.sessionBundles.sessionBundleTypeMonthly}
                        </span>
                      )}
                      {hasComparedPrice && (
                        <span className="inline-flex px-2 py-0.5 text-xs font-bold bg-yellow-400 text-gray-900 rounded-full">
                          -{discountPercent}%
                        </span>
                      )}
                    </div>
                    {getDescription(item) && (
                      <div className="text-gray-700 text-sm mt-1">{getDescription(item)}</div>
                    )}
                  </td>
                  <td className="px-4 py-4 text-gray-700 text-center">
                    {item.sessions}
                    {isSubscription && '/mo'}
                  </td>
                  {hasAnyDiscount && (
                    <td className="px-4 py-4 text-gray-400 line-through text-center">
                      {hasComparedPrice ? formatPrice(comparedPrice!) : '-'}
                    </td>
                  )}
                  <td className="px-4 py-4 font-bold text-gray-900 text-center">{formatPrice(item.price)}</td>
                  <td className="px-4 py-4 text-gray-700 text-center">
                    {pricePerSession ? formatPrice(pricePerSession) : '-'}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <button
                      onClick={() => onPurchase(item.id)}
                      className={`${
                        isSubscription
                          ? 'bg-green-600 hover:bg-green-700'
                          : 'bg-blue-600 hover:bg-blue-700'
                      } text-white py-2 px-4 rounded-md transition-colors font-medium text-sm`}
                    >
                      {isSubscription ? t.sessionBundles.subscribe : t.sessionBundles.purchase}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="md:hidden">
        {items.map((item, index) => {
          const comparedPrice = getComparedPrice(item);
          const hasComparedPrice = comparedPrice && comparedPrice > item.price;
          const pricePerSession = item.sessions && item.sessions > 0
            ? item.price / item.sessions
            : null;
          const isSubscription = item.type === 'subscription';
          const discountPercent = hasComparedPrice ? Math.round((1 - item.price / comparedPrice!) * 100) : 0;

          return (
            <div
              key={item.id}
              onClick={() => onPurchase(item.id)}
              className={`grid grid-cols-[1.5fr_1fr_auto] gap-2 p-4 border-b border-promo-200 cursor-pointer ${index % 2 === 0 ? 'bg-white' : 'bg-promo-50'}`}
            >
              <div>
                <div className="font-medium text-gray-900">
                  {getTranslatedField(item, 'name', language)}
                </div>
                {isSubscription && (
                  <span className="inline-flex px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full mt-1">
                    {t.sessionBundles.sessionBundleTypeMonthly}
                  </span>
                )}
                <div className="text-sm text-gray-500 mt-1">
                  {getDescription(item)}
                </div>
                <div className="text-sm text-gray-700 mt-1">
                  {item.sessions}{isSubscription && '/mo'} {t.pricing.sessions.toLowerCase()}
                </div>
              </div>
              <div>
                {hasComparedPrice && (
                  <div className="text-gray-400 line-through text-sm">{formatPrice(comparedPrice!)}</div>
                )}
                <div className="font-bold text-gray-900 flex items-center gap-1">
                  {formatPrice(item.price)}
                  {hasComparedPrice && (
                    <span className="inline-flex px-1.5 py-0.5 text-xs font-bold bg-yellow-400 text-gray-900 rounded-full">
                      -{discountPercent}%
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-700 mt-1">
                  {pricePerSession ? `${formatPrice(pricePerSession)}/session` : '-'}
                </div>
              </div>
              <div className="flex items-center justify-end">
                <button
                  onClick={(e) => { e.stopPropagation(); onPurchase(item.id); }}
                  className={`${
                    isSubscription
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-blue-600 hover:bg-blue-700'
                  } text-white py-2 px-3 rounded-md transition-colors font-medium text-sm`}
                >
                  {isSubscription ? t.sessionBundles.subscribe : t.sessionBundles.purchase}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default PromotionsTable;
